import { z } from 'zod';
import { isValidTeamSeatCount, roomHasTeams } from '../constants/teams.js';
import {
  BOT_TIER_ORDER,
  MAX_CONCURRENT_PLAYERS_HARD_LIMIT,
  MAX_ROOMS_HARD_LIMIT,
  MAX_WORLD_SIZE,
  MIN_WORLD_SIZE,
  ROOM_DEVICE_RULES,
  DEATHMATCH_ROUND_SECONDS,
  MAX_DEATHMATCH_ROUND_SECONDS,
  ROOM_MODES,
  ROOM_MAX_PLAYERS,
  ROOM_NAME_MAX_LENGTH,
  SKILL_MAX,
  SKILL_MIN,
  WORLD_HEIGHT,
  WORLD_WIDTH,
} from '../constants/index.js';
import type { BotSkillTier, RoomAccessRules, RoomMode, RoomSettings } from '../types/index.js';

/**
 * Room settings validation, in one place because two untrusted boundaries need exactly the same
 * rules: the worker control plane parses an operator's `PATCH /rooms/:id/settings`, and the
 * admin tRPC router parses the console's. Two copies would be two sets of bounds to drift apart,
 * and the looser one would be the one that mattered.
 *
 * `.strict()` on both: an unknown key here is a typo'd knob that would otherwise be accepted and
 * silently ignored, which is the worst way for an operator to find out a setting did nothing.
 */

/** A spawn weight. Non-negative and finite; the set of them may not be all zero. */
const tierWeightSchema = z.number().finite().nonnegative();

/**
 * The full weight table — every tier or none. A partial table has no meaning: weights are only
 * ever read as a share of their own sum, so a missing tier is not "leave it alone", it is zero.
 */
export const botTierWeightsSchema = z
  .object({
    novice: tierWeightSchema,
    casual: tierWeightSchema,
    skilled: tierWeightSchema,
    expert: tierWeightSchema,
  })
  .strict()
  .refine(
    (weights) => BOT_TIER_ORDER.some((tier) => weights[tier] > 0),
    'At least one tier weight must be greater than zero',
  ) satisfies z.ZodType<Record<BotSkillTier, number>>;

const maxPlayersSchema = z.number().int().min(1).max(ROOM_MAX_PLAYERS);
const seatCountSchema = z.number().int().nonnegative().max(ROOM_MAX_PLAYERS);

/**
 * One side of the room's world, in world units.
 *
 * **Integer**, because a coordinate goes on the wire quantised to a whole unit
 * (`constants/wire.ts`) and a wall at 6000.5 would be a wall a client could never draw a body
 * against exactly. Bounded at MIN_WORLD_SIZE / MAX_WORLD_SIZE — the absolute refusals, not the
 * measured-safe band, which is `SAFE_WORLD_SIZE` and `worldSizeWarnings`' business exactly as
 * `maxPlayers`' measured range is `hostLimitWarnings`'. Above MAX_WORLD_SIZE a confirmation
 * cannot reach it: a number that large is a typo rather than a decision.
 *
 * **Defaulted**, and that is load-bearing rather than a convenience: `room_roster` rows written
 * before this field existed hold a settings blob with no walls in it, and they are re-validated
 * with this schema on the way out (`services/rooms/roster.service.ts`, which *skips* a row it
 * cannot parse). A default is what makes every one of them read forward as the 6000 x 6000 room
 * it has always been, with no migration and no lost declaration.
 */
const worldWidthSchema = z
  .number()
  .int()
  .min(MIN_WORLD_SIZE)
  .max(MAX_WORLD_SIZE)
  .default(WORLD_WIDTH);
const worldHeightSchema = z
  .number()
  .int()
  .min(MIN_WORLD_SIZE)
  .max(MAX_WORLD_SIZE)
  .default(WORLD_HEIGHT);

/**
 * Which match loop the room runs — see `RoomMode`.
 *
 * **Defaulted, and that is the upgrade path rather than a convenience**: the world size's reason
 * exactly, and `access`' after it. `room_roster` rows written before this field existed hold a
 * settings blob with no `mode` in it, and `roster.service.ts` re-validates every row with
 * `roomSettingsSchema` on the way out and *skips* one it cannot parse — so a required field here
 * would have silently deleted every declaration a deployment had. With the default, every one of
 * them reads forward as the classic room it has always been.
 *
 * The enum is `ROOM_MODES` rather than a list written out here, so the modes this schema accepts
 * and the modes the console can offer are the same list.
 */
const roomModeSchema = z.enum(ROOM_MODES).default('classic');

/**
 * The ceiling on a deathmatch ROUND, in seconds — see `RoomSettings.roundSeconds`.
 *
 * **Defaulted, for the upgrade path the walls and `access` and `mode` are all under**: a
 * `room_roster` row written before this field existed has no `roundSeconds` in it, and
 * `roster.service.ts` skips a row this schema cannot parse, so a required field would silently
 * delete every declaration a deployment has.
 *
 * **The default is a real limit rather than 0**, and that is deliberate: the field exists because
 * a round with no clock ran past ten minutes in a live room, so a deployment that says nothing
 * should get the behaviour the field was added for. An operator who wants the old behaviour asks
 * for it by typing 0.
 *
 * Bounded at MAX_DEATHMATCH_ROUND_SECONDS rather than left open — an hour is past any round
 * anybody wants, and a larger number is a typo rather than a decision. Integer, because it is
 * turned into whole ticks.
 */
const roundSecondsSchema = z
  .number()
  .int()
  .min(0)
  .max(MAX_DEATHMATCH_ROUND_SECONDS)
  .default(DEATHMATCH_ROUND_SECONDS);

/**
 * Who a room lets in: a skill band and a device restriction.
 *
 * **Defaulted at every level, and that is the upgrade path rather than a convenience.**
 * `room_roster` rows written before this field existed hold a settings blob with no `access` in
 * it, and `roster.service.ts` re-validates every row with `roomSettingsSchema` on the way out
 * and *skips* one it cannot parse — so a required field here would have silently deleted every
 * declaration a deployment had. The world-size fields carry defaults for exactly this reason and
 * this is that path again: an old row reads forward as the unrestricted room it has always been.
 *
 * The whole object is defaulted as well as each of its fields, because the field itself is
 * missing from those rows rather than merely incomplete.
 *
 * `.strict()`, like everything else here: `{ minSkil: 40 }` is a typo that would otherwise be
 * accepted and do nothing, which is the worst way for an operator to find out about it.
 */
const roomAccessShape = z
  .object({
    minSkill: z.number().int().min(SKILL_MIN).max(SKILL_MAX).default(SKILL_MIN),
    maxSkill: z.number().int().min(SKILL_MIN).max(SKILL_MAX).default(SKILL_MAX),
    devices: z.enum(ROOM_DEVICE_RULES).default('any'),
  })
  .strict()
  .superRefine((access, ctx) => {
    // An inverted band admits nobody at all, which is never what an operator meant to type and
    // is indistinguishable from a closed room once it is saved.
    if (access.minSkill > access.maxSkill) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minSkill'],
        message: 'The lowest skill admitted must not be above the highest',
      });
    }
  }) satisfies z.ZodType<RoomAccessRules, z.ZodTypeDef, unknown>;

const roomAccessSchema = roomAccessShape.default({});

/**
 * Cross-field bounds, applied to whatever of the pair is present.
 *
 * On the patch schema only one half of a pair may arrive, and the other half is whatever the
 * room already has — which this schema cannot see. So a patch is checked for the contradictions
 * it *does* carry and the rest is the caller's merge to re-validate with `roomSettingsSchema`.
 */
function checkSeatBounds(
  settings: { mode?: RoomMode; maxPlayers?: number; minPlayers?: number; maxBots?: number },
  ctx: z.RefinementCtx,
): void {
  const { mode, maxPlayers, minPlayers, maxBots } = settings;
  if (maxPlayers === undefined) return;
  /**
   * **A TEAM ROOM'S SEAT COUNT MUST BE EVEN.**
   *
   * Andy: "We should be able to set the number of players in the room and it must be a multiple
   * of 2 (and even number)." An odd cap cannot be split down the middle, so a room declared with
   * one would have two permanently unequal sides — which is the one thing a team game may not be,
   * because every other rule in the mode is a comparison between them.
   *
   * Refused here rather than rounded, on the walls' rule: an operator who typed 21 meant
   * something, and a schema that silently made it 20 or 22 would be a console lying about what it
   * saved. The rule is `isValidTeamSeatCount`, which the lobby's own odd-number case does NOT go
   * through — an odd number of humans who turned up is a fact about the evening rather than about
   * the declaration, and `beginRound` settles it by drawing which side gets the spare.
   */
  if (mode !== undefined && roomHasTeams(mode) && !isValidTeamSeatCount(maxPlayers)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maxPlayers'],
      message: 'A two-sided room needs an even number of seats, so the sides can be equal',
    });
  }
  if (minPlayers !== undefined && minPlayers > maxPlayers) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['minPlayers'],
      message: 'minPlayers must not exceed maxPlayers',
    });
  }
  if (maxBots !== undefined && maxBots > maxPlayers) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maxBots'],
      message: 'maxBots must not exceed maxPlayers',
    });
  }
}

/**
 * A complete settings object, as stored on a room and reported in a `RoomSummary`.
 *
 * The third type argument is `unknown` rather than the default (`RoomSettings`) only because the
 * two world fields, `access` and `mode` carry defaults, which makes them optional on the way
 * *in*. What the
 * `satisfies` is here to guarantee is unchanged and is the half that matters: what comes out of
 * this schema is a `RoomSettings`, with every field present.
 */
export const roomSettingsSchema = z
  .object({
    mode: roomModeSchema,
    roundSeconds: roundSecondsSchema,
    maxPlayers: maxPlayersSchema,
    botsEnabled: z.boolean(),
    minPlayers: seatCountSchema,
    maxBots: seatCountSchema,
    tierWeights: botTierWeightsSchema,
    access: roomAccessSchema,
    worldWidth: worldWidthSchema,
    worldHeight: worldHeightSchema,
  })
  .strict()
  .superRefine(checkSeatBounds) satisfies z.ZodType<RoomSettings, z.ZodTypeDef, unknown>;

/**
 * A change to some of them. Every field optional, so `{}` is a legal no-op — an empty patch is
 * an operator who changed their mind, not an error.
 */
export const roomSettingsPatchSchema = z
  .object({
    // Unwrapped for the walls' reason: a patch is a diff, so an absent key is "leave the mode
    // alone" rather than "put this room back to classic" — which is the one thing a change to a
    // deathmatch declaration's bot mix must never also mean.
    mode: roomModeSchema.removeDefault(),
    maxPlayers: maxPlayersSchema,
    botsEnabled: z.boolean(),
    minPlayers: seatCountSchema,
    maxBots: seatCountSchema,
    tierWeights: botTierWeightsSchema,
    // Unwrapped on the walls' rule: a patch is a diff, so an absent key is "leave the round
    // limit alone" rather than "put it back to the default".
    roundSeconds: roundSecondsSchema.removeDefault(),
    // Unwrapped for the walls' reason below: an absent key is "leave the rules alone", not
    // "open the room to everybody". Present, it replaces the rules WHOLE — `tierWeights`'
    // treatment, and for its reason: a band is only meaningful as a pair, so a patch carrying
    // one half of one would be an operator setting a floor and getting a ceiling they did not
    // ask for. The inner defaults still apply, so `{ minSkill: 40 }` is a floor of 40 with the
    // ceiling and the device rule back at their defaults.
    access: roomAccessSchema.removeDefault(),
    // Unwrapped, so an absent key stays absent rather than becoming "set it back to 6000": a
    // patch is a diff, and the default belongs to the whole object above.
    worldWidth: worldWidthSchema.removeDefault(),
    worldHeight: worldHeightSchema.removeDefault(),
  })
  .partial()
  .strict()
  .superRefine(checkSeatBounds);

/**
 * A change to the settings of a room that is ALREADY RUNNING.
 *
 * The same patch, minus the walls and minus the mode. A room's world size is geometry it was
 * built inside — the spikes and the hotspots were placed against it, every clamp in `step` reads
 * it, and each viewer set its camera bounds and its minimap scale from the size named in
 * `welcome` — so there is no honest way to move it under people who are playing. It is refused
 * here rather than silently dropped, because an operator whose save did nothing is the worst way
 * to find that out, and it is refused in `@chompdome/shared` rather than in either caller because
 * both the worker control plane and the admin router have to give the same answer.
 *
 * A declaration's size is edited on the roster (`admin.rooms.update`) and takes effect the next
 * time the room is instantiated.
 *
 * **The mode is refused for the same reason one level up.** A deathmatch room's whole match loop
 * is built around it: the lobby it filled, the roster it enrolled and the round it spawned
 * everybody into at once all happened because the room opened in that mode, so switching a
 * running room to `classic` mid-round leaves a round with no loop to finish it, and switching one
 * the other way starts a match half of whose players are already the size of the map. It is not
 * geometry, but it is fixed at construction exactly as the geometry is, which is why it sits
 * beside the walls here rather than beside `access` below. The roster is where a mode is chosen
 * and it applies the next time the room opens.
 *
 * **`access` is deliberately NOT in that refusal, and the difference is the point.** The walls
 * are geometry every body in the room was placed against; access rules are a rule about the
 * *door*, read where a player is placed and where their ticket is signed and nowhere else. So
 * tightening them on a running room is coherent and cheap: it decides who may come in next, it
 * evicts nobody — a player already inside met the rules that were in force when they were let
 * in — and no phase of `step` has to be told. That also means a room does not empty out when an
 * operator narrows its band, which is the behaviour an operator would otherwise have to be
 * warned about.
 */
export const liveRoomSettingsPatchSchema = roomSettingsPatchSchema.superRefine((patch, ctx) => {
  // A sentence of its own rather than a third entry in the loop below: the walls and the mode are
  // refused for the same *kind* of reason and an operator is owed the actual one.
  if (patch.mode !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['mode'],
      message:
        "A running room's mode cannot be changed — the round it is playing, the players it " +
        'enrolled and the way it spawned them were all decided when it opened. Edit the room on ' +
        'the roster and the new mode applies the next time it opens.',
    });
  }
  for (const key of ['worldWidth', 'worldHeight'] as const) {
    if (patch[key] === undefined) continue;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [key],
      message:
        "A running room's world size cannot be changed — the spikes, the food and every player " +
        'in it were placed inside these walls. Edit the room on the roster and the new size ' +
        'applies the next time it opens.',
    });
  }
});

export type RoomSettingsInput = z.infer<typeof roomSettingsSchema>;
export type RoomSettingsPatch = z.infer<typeof roomSettingsPatchSchema>;

/**
 * The name an operator gives a roster room.
 *
 * Trimmed and bounded rather than free text: it is drawn in a table beside a room id, and a name
 * with leading whitespace or three hundred characters of it is a table nobody can read. The
 * emptiness check is on the *trimmed* value, so a name of spaces is a missing name.
 *
 * The bound is `ROOM_NAME_MAX_LENGTH`, shared with `roomInfoSchema`: this name goes on the
 * `welcome` frame of every room woken from this declaration, so a name that saves here and would
 * not encode there is a declaration nobody could join.
 */
export const rosterRoomNameSchema = z.string().trim().min(1).max(ROOM_NAME_MAX_LENGTH);

/**
 * A change to a host's capacity knobs.
 *
 * Bounded here at the absolute refusals — `MAX_ROOMS_HARD_LIMIT`, `ROOM_MAX_PLAYERS` and
 * `MAX_CONCURRENT_PLAYERS_HARD_LIMIT` — and NOT at the measured-safe range: an operator is
 * allowed to leave that range deliberately, with a confirmation, and `hostLimitWarnings` is what
 * decides whether one is needed. A schema that refused outright would make the confirmation
 * unreachable; one that had no ceiling at all would let a typo through.
 *
 * Note where `maxPlayers` is bounded: at ROOM_MAX_PLAYERS (200), the simulation's ceiling, and
 * not at DEFAULT_ROOM_PLAYERS (100), which is only what was measured. The measured figure is the
 * confirmation's business, not the schema's.
 */
export const hostLimitsPatchSchema = z
  .object({
    maxRooms: z.number().int().min(1).max(MAX_ROOMS_HARD_LIMIT),
    maxPlayers: z.number().int().min(1).max(ROOM_MAX_PLAYERS),
    maxConcurrentPlayers: z.number().int().min(1).max(MAX_CONCURRENT_PLAYERS_HARD_LIMIT),
    /**
     * Close this host to rooms the matchmaker would invent. Unbounded by anything here — there
     * is no measurement to be outside of, and the guard that matters is a *fleet* one
     * (`hostLockWarnings`), which no schema over a single patch can see.
     */
    locked: z.boolean(),
  })
  .partial()
  .strict();

export type HostLimitsPatch = z.infer<typeof hostLimitsPatchSchema>;
